/*
  Файлы одного шага - журнал загрузок, отобранный по виду файла (01.10.2026).

  🔴 Указание владельца: «на каждом шаге должны быть файлы только этого шага,
  иначе определять, загрузили файл в шаг, нельзя». До этого дня журнал был один
  на весь раздел и стоял внизу страницы общей лентой: в нём вперемешку лежали
  выгрузки начислений и выписки, а реестра с домовой книгой не было вовсе - они
  не оставляли следа. Человек, открывший шаг «Реестр должников», не мог ответить
  на простейший вопрос: грузил он сюда файл или нет.

  Отбор делает СЕРВЕР, а не этот компонент: вид файла уходит в запрос, и сервер
  отбирает строки ДО среза на страницы. Отсеки мы чужие строки здесь - шаг показал бы
  «загрузок ещё не было» при загруженном накануне файле.

  С 21.10.2026 страницы тоже режет сервер (требование владельца: пагинация серверная):
  журнал просит окно (`limit`, `offset`) и получает `{ items, total }`. Прежний потолок
  «последние пятьдесят строк» снят - журнал читается весь, по страницам.

  Догоняющее обновление перенесено сюда со страницы. Причина прежняя
  (11.09.2026): на чистой базе человек получал «записано 2 строки» и рядом, в
  тот же миг, «загрузок ещё не было» - журнал запрашивался один раз сразу после
  ответа сервера и, промахнувшись, больше не переспрашивал. Поэтому первая страница
  перезапрашивается, пока в ней не окажется строка ИМЕННО этой загрузки (она самая
  свежая, значит стоит на первой странице).
*/

import { useCallback } from 'react'

import { api } from '../api/client.js'
import { Notice } from './Common.jsx'
import ImportJournal from './ImportJournal.jsx'
import usePagedList from './usePagedList.js'

/* Сколько раз переспросить журнал и с какой паузой. Четырёх попыток с паузой в
   четыреста миллисекунд хватает с запасом; дольше ждать незачем - человек уже
   видит разбор файла, а журнал догонит при следующем открытии шага. */
const ATTEMPTS = 4
const PAUSE = 400

function pause(ms) {
  return new Promise((done) => setTimeout(done, ms))
}

/* Заголовок называет МЕСТО, в котором стоит журнал: в ленте это шаг, во вкладке
   «Начисления файлом» или «Поступления» - вкладка. Слово «шаг» там было бы
   неправдой: шагов в этих вкладках нет. */
const STEP_TITLE = 'Файлы, загруженные в этот шаг'

export default function StepJournal({ kind, awaiting, title = STEP_TITLE }) {
  const load = useCallback(
    async (window) => {
      /* Догоняем только первую страницу: ожидаемая загрузка свежее всех, и на
         следующих её искать незачем. Ответ ушедшего запроса хук отбросит сам:
         человек переключает шаги быстрее, чем отвечает сервер. */
      let answer = await api.imports(kind, window)
      for (let attempt = 1; attempt < ATTEMPTS; attempt += 1) {
        const caughtUp =
          !awaiting ||
          window.offset > 0 ||
          answer.items.some((run) => run.import_run_id === awaiting)
        if (caughtUp) break
        await pause(PAUSE)
        answer = await api.imports(kind, window)
      }
      return answer
    },
    [kind, awaiting],
  )
  /* Решение об отложенном погашении меняет строку журнала - `reload` перечитывает окно.

     Новая загрузка (`awaiting`) возвращает журнал на первую страницу (23.10.2026, находка
     проверки): её строка самая свежая и стоит первой, а на второй странице условие
     `window.offset > 0` выключало догоняющее обновление - новой строки не было видно вовсе. */
  const { rows, error, reload, bar } = usePagedList(load, undefined, 0, awaiting)

  return (
    <section className="section">
      <h2 className="section__title">{title}</h2>
      <Notice error={error} />
      {/* При отказе - только отказ (23.10.2026): журнал без строк рисовал рядом с ошибкой
          бесконечное «загружаем», хотя ждать уже нечего. */}
      {error ? null : <ImportJournal journal={rows} bar={bar} onChanged={reload} />}
    </section>
  )
}
